import React from 'react';
import PropTypes from 'prop-types';

import {Box, ListItem, ListItemText, Typography, Link, IconButton} from 'views/components';
import {CheckCircle as CheckCircleIcon} from 'views/icons';
import {Translate} from 'views/module';

import Backend from 'utils/backend';

const SlipView = (props) => {
  const [status, setStatus] = React.useState('IN_PROGRESS');

  const item = props.item;
  const statusProgressWrap = props.statusProgressWrap;
  const weekOf = props.weekOf;

  const hourlyRate = item.hourlyRate != null ? (Math.round(item.hourlyRate * 100) / 100).toFixed(2) : null;

  const hours = item.hours;
  const confirmedHours = hours.confirmedHours != null ? Number(hours.confirmedHours.toFixed(2)) : null;
  const totalHours = hours.totalHours != null ? Number(hours.totalHours.toFixed(2)) :null;

  // Cost is derived from the holiday-weighted pay hours (1.5x on stat holidays). Fall back to
  // actual hours for slips tracked before the pay-hours fields existed. Displayed hours stay actual.
  const confirmedPayHours = hours.confirmedPayHours != null ? hours.confirmedPayHours : hours.confirmedHours;
  const totalPayHours = hours.totalPayHours != null ? hours.totalPayHours : hours.totalHours;
  const hasHoliday =
    (hours.confirmedPayHours != null && hours.confirmedPayHours > hours.confirmedHours) ||
    (hours.totalPayHours != null && hours.totalPayHours > hours.totalHours);

  let confirmedCost = null;
  let totalCost = null;

  if (confirmedPayHours != null && hourlyRate != null) {
    confirmedCost = (confirmedPayHours * hourlyRate).toFixed(2);
  }
  if (totalPayHours != null && hourlyRate != null) {
    totalCost = (totalPayHours * hourlyRate).toFixed(2);
  }

  const expense = item.expenses;

  React.useEffect(() => {
    setStatus(item.status);
  }, [item]);

  const onComplete = () => {
    statusProgressWrap(async () => {
      try {
        setStatus('COMPLETE');
        await Backend.paymentSlip.markClientSlipCompete(item.clientRn, weekOf);
      } catch (e) {
        setStatus('IN_PROGRESS');
        throw e;
      }
    }, {debugString: 'CLIENT_PAYMENT_SLIP_MARK_COMPLETE_DASHBORD'});
  };

  return (
    <ListItem
      divider
      secondaryAction={
        <>
          {
            status === 'IN_PROGRESS' &&
            <IconButton
              onClick={onComplete}
              color="warning"
            >
              <CheckCircleIcon
                fontSize="medium"
              />
            </IconButton>
          }
          {
            status !== 'IN_PROGRESS' &&
            <CheckCircleIcon
              fontSize="medium"
              color="success"
              sx={{mr: 1, mt: 1}}
            />
          }
        </>
      }
    >
      <ListItemText
        primary={
          <Link
            href={`/client/${item.client.clientRn}`}
            sx={{textDecoration: 'none', color: '#1976d2', fontWeight: 500, fontSize: '1em'}}
          >
            {item.client.name.trim()}
          </Link>
        }
        secondary={
          <Box sx={{display: 'grid'}}>
            <Typography variant="caption" color="text.secondary">
              <Translate
                expression='dashboardWidgets.ClientPaymentStatus.ConfirmedHours'
                substitute={{confirmedHours}}
              />

              {
                hourlyRate &&
                <>
                  {` | `}
                  <Translate
                    expression='dashboardWidgets.ClientPaymentStatus.ConfirmedCost'
                    substitute={{confirmedCost}}
                  />
                </>
              }
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Translate
                expression='dashboardWidgets.ClientPaymentStatus.TotalHours'
                substitute={{totalHours}}
              />
              {
                hourlyRate &&
                <>
                  {` | `}
                  <Translate
                    expression='dashboardWidgets.ClientPaymentStatus.ConfirmedTotal'
                    substitute={{totalCost}}
                  />
                </>
              }
            </Typography>
            {
              hasHoliday && hourlyRate &&
              <Typography variant="caption" sx={{color: '#b26a00', fontWeight: 600}}>
                <Translate expression='dashboardWidgets.ClientPaymentStatus.StatHoliday' />
              </Typography>
            }
            {
              Boolean(expense) &&
              <Typography variant="caption" color="text.secondary">
                <Translate
                  expression='dashboardWidgets.ClientPaymentStatus.Expenses'
                  substitute={{expense}}
                />
              </Typography>
            }
          </Box>
        }
        primaryTypographyProps={{variant: 'subtitle1'}}
        secondaryTypographyProps={{component: 'div'}}
      />
    </ListItem>
  );
};

SlipView.propTypes = {
  item: PropTypes.object.isRequired,
  weekOf: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

export default React.memo(SlipView);
